<script setup>
import { computed, onMounted, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { useRoute, useRouter } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ResourceHelpActions from 'dashboard/components/widgets/ResourceHelpActions.vue';
import { CONTEXTUAL_HELP_KEYS } from 'dashboard/constants/contextualHelp';
import ConversationApi from 'dashboard/api/inbox/conversation';
import {
  PRODUCT_TOUR_TYPES,
  useProductTour,
} from 'dashboard/composables/useProductTour';

const props = defineProps({
  pageTitle: { type: String, required: true },
  contactFilter: { type: Object, default: null },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  isCompactControls: { type: Boolean, default: false },
  isUltraCompact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
]);

const { uiSettings, updateUISettings } = useUISettings();
const helpSystemKey = CONTEXTUAL_HELP_KEYS.DASHBOARD_INBOX;
const { startIfPending, startTour } = useProductTour(helpSystemKey);
const route = useRoute();
const router = useRouter();
const showCompactActions = ref(false);

const waitForConversationView = async (attempt = 0) => {
  if (document.querySelector('#conversation-messages-view')) return true;
  if (attempt >= 80) return false;

  await new Promise(resolve => {
    setTimeout(resolve, 100);
  });
  return waitForConversationView(attempt + 1);
};

const prepareDashboardTour = async () => {
  if (
    document.querySelector('#dashboard-inbox-first-conversation') ||
    document.querySelector('#conversation-messages-view')
  ) {
    return;
  }

  try {
    const response = await ConversationApi.get({
      assigneeType: 'all',
      status: 'all',
      sortBy: 'last_activity_at_desc',
      page: 1,
      withMessages: true,
    });
    const [conversation] = response.data.data.payload;
    if (!conversation) return;

    await router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: conversation.id,
      },
    });
    await waitForConversationView();
  } catch {
    // The general tour remains available when no conversation can be opened.
  }
};

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const toggleCompactActions = () => {
  showCompactActions.value = !showCompactActions.value;
};

const closeCompactActions = () => {
  showCompactActions.value = false;
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

// While filters narrow the list, the header names it and the back button exits.
const showFilterScope = computed(
  () => props.hasAppliedFilters && !props.hasActiveFolders
);

// The contact scope is set from the contact panel; it is exited, not edited.
const isContactScoped = computed(
  () => showFilterScope.value && !!props.contactFilter
);

const title = computed(
  () => (isContactScoped.value && props.contactFilter.name) || props.pageTitle
);

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};

onMounted(async () => {
  if (route.query.product_tour === helpSystemKey) {
    await prepareDashboardTour();
    await startTour({ markAsCompleted: false });
    router.replace({ query: { ...route.query, product_tour: undefined } });
    return;
  }

  startIfPending({
    tourType: PRODUCT_TOUR_TYPES.WELCOME,
    beforeStart: prepareDashboardTour,
  });
});
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 h-[3.25rem]"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div class="flex items-center justify-center min-w-0">
      <NextButton
        v-if="showFilterScope"
        v-tooltip.right="$t('FILTER.CLEAR_BUTTON_LABEL')"
        :aria-label="$t('FILTER.CLEAR_BUTTON_LABEL')"
        icon="i-lucide-chevron-left"
        class="shrink-0 -ms-2 !h-6 !w-6 me-1"
        slate
        sm
        ghost
        @click="emit('resetFilters')"
      />
      <h1 class="text-base font-medium truncate text-n-slate-12" :title="title">
        {{ title }}
      </h1>
      <span
        v-if="
          allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
        "
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
        :title="allCount"
      >
        {{ formattedAllCount }}
      </span>
      <span
        v-if="!hasAppliedFiltersOrActiveFolders && !isUltraCompact"
        class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
      >
        {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
      </span>
    </div>
    <div
      v-on-click-outside="closeCompactActions"
      class="relative flex items-center"
    >
      <NextButton
        v-if="isCompactControls"
        v-tooltip.left="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        icon="i-lucide-more-vertical"
        slate
        xs
        faded
        @click="toggleCompactActions"
      />
      <div
        v-if="!isCompactControls || showCompactActions"
        class="flex items-center gap-1"
        :class="{
          'absolute top-full z-50 mt-1 rounded-lg border border-n-weak bg-n-alpha-3 p-1 shadow-lg backdrop-blur-[100px] ltr:right-0 rtl:left-0':
            isCompactControls,
        }"
      >
        <div
          id="conversation-list-view-controls"
          class="flex items-center gap-1"
          :class="{ 'flex-col': isCompactControls }"
        >
          <template v-if="hasAppliedFilters && !hasActiveFolders">
            <div class="relative">
              <NextButton
                v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
                icon="i-lucide-save"
                slate
                xs
                faded
                @click="emit('addFolders')"
              />
              <div
                id="saveFilterTeleportTarget"
                class="absolute z-50 mt-2"
                :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
              />
            </div>
            <NextButton
              v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
              icon="i-lucide-circle-x"
              ruby
              faded
              xs
              @click="emit('resetFilters')"
            />
          </template>
          <template v-if="hasActiveFolders">
            <div class="relative">
              <NextButton
                id="toggleConversationFilterButton"
                v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
                icon="i-lucide-pen-line"
                slate
                xs
                faded
                @click="emit('filtersModal')"
              />
              <div
                id="conversationFilterTeleportTarget"
                class="absolute z-50 mt-2"
                :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
              />
            </div>
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
              icon="i-lucide-trash-2"
              ruby
              xs
              faded
              @click="emit('deleteFolders')"
            />
          </template>
          <div v-else-if="!isContactScoped" class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
              icon="i-lucide-list-filter"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <ConversationBasicFilter
            v-if="!isContactScoped"
            :is-on-expanded-layout="isOnExpandedLayout"
            :show-status-filter="!hasAppliedFiltersOrActiveFolders"
            @change-filter="onBasicFilterChange"
          />
          <SwitchLayout
            :is-on-expanded-layout="isOnExpandedLayout"
            @toggle="toggleConversationLayout"
          />
        </div>
        <ResourceHelpActions
          id="dashboard-inbox-help-actions"
          :system-key="helpSystemKey"
          :before-start="prepareDashboardTour"
        />
      </div>
    </div>
  </div>
</template>
